/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EmployeeAbsenceFields.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EmployeeAbsenceFields - Периоды отсутствия в карточной секции.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: карточки периодов, нумерация, пустое состояние в рамке.
 * LAST_CHANGE: 2026-07-13 - UX: плавное появление строк отсутствий (Collapse appear).
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций и парные END_METHOD.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import AppDateField from '../../../components/AppDateField';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';

let nextLocalId = 1;

export function createEmptyAbsence() {
  return {
    localId: nextLocalId++,
    from: '',
    to: '',
  };
}

export function isAbsenceValid(absence) {
  const hasFrom = Boolean(absence.from);
  const hasTo = Boolean(absence.to);

  if (!hasFrom && !hasTo) {
    return true;
  }

  if (!hasFrom || !hasTo) {
    return false;
  }

  return absence.from <= absence.to;
}

export function areAllAbsencesValid(absences) {
  return absences.every(isAbsenceValid);
}

export function buildAbsencesPayload(absences) {
  return absences
    .filter((item) => item.from && item.to)
    .map((item) => ({
      from: item.from,
      to: item.to,
    }));
}

export function absencesToFormState(absences) {
  return (absences ?? []).map((item) => ({
    localId: nextLocalId++,
    from: item.from ?? '',
    to: item.to ?? '',
  }));
}

/* START_METHOD_EmployeeAbsenceFields */
/* START_CONTRACT: EmployeeAbsenceFields
 * PURPOSE: Редактируемый список периодов отсутствия сотрудника с валидацией.
 * INPUTS: { absences: object[], onChange: (list)=>void, disabled: boolean }.
 * OUTPUTS: { JSX.Element - поля отсутствий }.
 * SIDE_EFFECTS: нет (делегирует onChange).
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: EmployeeAbsenceFields
 */
export default function EmployeeAbsenceFields({
  absences,
  onChange,
  disabled,
}) {
  const handleFieldChange = (localId, field, value) => {
    onChange(
      absences.map((item) => (item.localId === localId
        ? { ...item, [field]: value }
        : item))
    );
  };

  const handleAdd = () => {
    onChange([...absences, createEmptyAbsence()]);
  };

  const handleRemove = (localId) => {
    onChange(absences.filter((item) => item.localId !== localId));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {absences.length > 0
            ? `${absences.length} ${absences.length === 1 ? 'период' : absences.length < 5 ? 'периода' : 'периодов'}`
            : 'Периоды не заданы'}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={disabled}
        >
          Добавить период
        </Button>
      </Box>

      {!absences.length ? (
        <Paper
          variant="outlined"
          sx={{
            py: 2.5,
            px: 2,
            textAlign: 'center',
            bgcolor: 'background.default',
          }}
        >
          <EventBusyOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            Отпуска и больничные учитываются при планировании загрузки.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {absences.map((absence, index) => {
            const hasFrom = Boolean(absence.from);
            const hasTo = Boolean(absence.to);
            const partiallyFilled = hasFrom !== hasTo;
            const invalidRange = hasFrom && hasTo && !isAbsenceValid(absence);
            const invalid = partiallyFilled || invalidRange;

            return (
              <Collapse key={absence.localId} in appear>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderColor: invalid ? 'error.main' : 'divider',
                    bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Период {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(absence.localId)}
                      disabled={disabled}
                      aria-label={`Удалить отсутствие ${index + 1}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <AppDateField
                      label="С"
                      value={absence.from}
                      onChange={(nextValue) => handleFieldChange(absence.localId, 'from', nextValue)}
                      disabled={disabled}
                      error={invalid}
                      sx={{ flex: 1 }}
                    />
                    <AppDateField
                      label="По"
                      value={absence.to}
                      onChange={(nextValue) => handleFieldChange(absence.localId, 'to', nextValue)}
                      disabled={disabled}
                      error={invalid}
                      helperText={
                        partiallyFilled
                          ? 'Укажите обе даты'
                          : invalidRange
                            ? 'Дата окончания раньше начала'
                            : undefined
                      }
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Paper>
              </Collapse>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
/* END_METHOD_EmployeeAbsenceFields */
